import { useEffect, useMemo, useRef } from "react";
import { BuildingForm } from "../../components/forms/BuildingForm";
import type { RuleFormData } from "../../components/forms/BuildingForm";
import { StatusMessage } from "../../components/ui";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import Modal from "../../components/ui/Modal";
import type { CreateCustomerRequest } from "../../services/customer/customer.types";
import { useBuildingRules } from "./useBuildingRules";
import type { ConfirmBuildingAction, CustomerWithSite } from "./buildingRules";

interface Props {
  customer: CustomerWithSite | null;
  submitting: boolean;
  error: string | null;
  success: string | null;
  onError: (message: string | null) => void;
  onSuccess: (message: string | null) => void;
  confirm: ConfirmBuildingAction;
  onChanged: () => Promise<void>;
  onSiteDeleted: (name: string) => void;
  onSave: (values: CreateCustomerRequest, rules: RuleFormData[] | null) => Promise<boolean>;
  onCancel: () => void;
}

export function BuildingEditor({ customer, submitting, error, success, onError, onSuccess, confirm, onChanged, onSiteDeleted, onSave, onCancel }: Props) {
  const contentRef = useRef<HTMLDivElement>(null);
  const rules = useBuildingRules({ customer, confirm, onChanged, onError, onSuccess });
  const busy = submitting || rules.submitting;
  const hasEditingRuleData = rules.editingRuleData !== null;
  useEffect(() => {
    if (hasEditingRuleData) {
      contentRef.current?.querySelector(".rule-edit-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [rules.editingRuleId, hasEditingRuleData]);
  const initialData = useMemo(() => customer ? {
    customerName: customer.customerName || "",
    address1: customer.address1 || "",
    address2: customer.address2 || "",
    townCity: customer.townCity || "",
    county: customer.county || "",
    country: customer.country || "",
    postCode: customer.postCode || "",
  } : undefined, [customer]);

  const submit = async () => {
    if (rules.editingRuleId !== null && rules.editingRuleData) {
      if (await rules.update(rules.editingRuleId, rules.editingRuleData)) onCancel();
      return;
    }
    const form = contentRef.current?.querySelector("form");
    if (!form) throw new Error("Building form is not mounted");
    if (typeof form.requestSubmit === "function") form.requestSubmit();
    else form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
  };

  return (
    <Modal isOpen title={customer ? "Edit Customer" : "Add Customer"}
      content={
        <div className="modal-form-wrapper" ref={contentRef}>
          {error && <StatusMessage kind="error" onClose={() => onError(null)}>{error}</StatusMessage>}
          {!error && success && <StatusMessage kind="success" onClose={() => onSuccess(null)}>{success}</StatusMessage>}
          {rules.loading ? <LoadingSpinner message="Loading customer rules…" /> : (
            <BuildingForm
              key={customer?.id ?? "new"}
              onSubmit={(values, ruleData) => { void onSave(values, ruleData ?? null); }}
              initialData={initialData}
              isLoading={busy}
              siteId={customer?.siteId}
              siteHierarchyId={customer?.siteHierarchyId}
              existingRules={rules.rules}
              existingRuleDetailsFromParent={rules.details}
              onExistingRuleDetailsChange={rules.setDetails}
              onDeleteRule={rules.remove}
              onActivateRule={rules.activate}
              onEditRule={ruleId => { void rules.edit(ruleId); }}
              editingRuleId={rules.editingRuleId}
              editingRuleData={rules.editingRuleData}
              onUpdateRule={(ruleId, data) => { void rules.update(ruleId, data); }}
              onCancelEditRule={rules.cancelEdit}
              onEditingRuleDataChange={rules.setEditingRuleData}
              onSiteDeleted={name => { onSiteDeleted(name); void onChanged(); }}
            />
          )}
        </div>
      }
      onConfirm={submit}
      onCancel={() => { if (!busy) onCancel(); }}
      okButtonLoading={busy || rules.loading}
      cancelButtonLoading={busy}
    />
  );
}
